import {
    useState, useEffect, useMemo, useCallback
} from 'react';

import { ApolloProvider } from '@apollo/client';
import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import {
    colors, Config, heapTrack, loadHeap,
} from '@modernanimal/common';
import { initializeApolloClient } from '@modernanimal/common/src/api';
import { useLoadScript, Libraries } from '@react-google-maps/api';
// eslint-disable-next-line import/no-extraneous-dependencies
import posthog from 'posthog-js';
import {
    Outlet, useLocation, useNavigate, useSearchParams
} from 'react-router-dom';
import useBreakpoint from 'use-breakpoint';

import AppointmentConfirmation from './AppointmentConfirmation';
import AppointmentSelection from './AppointmentSelection';
import { useLocationCreditCardSetting } from './AppointmentSelection/hooks';
import AddCard from './clientOnboarding/AddCard';
import ClientOnboarding from './clientOnboarding/ClientOnboarding';
import PhoneNumber from './clientOnboarding/PhoneNumber';
import PhoneVerification from './clientOnboarding/PhoneVerification';
import Fonts from './fonts';
import globalStyles from './globalStyles';
import HeapSubscriptions from './HeapSubscriptions';
import {
    BookingFlowScreenName,
    MOBILE_WIDTH_PX,
    BREAKPOINTS,
    STANDARD_BOOKING_FLOW_SCREENS,
    TEST_BOOKING_FLOW_SCREENS
} from './helpers';
import Icons from './icons';
import MedicalRecords from './patientOnboarding/MedicalRecords';
import PatientAttributes from './patientOnboarding/PatientAttributes';
import PatientBasics from './patientOnboarding/PatientBasics';
import ReasonForVisit from './patientOnboarding/ReasonForVisit';
import useWebBookingStore, { resetAllSlices } from './store';
import TestAppointmentSelection from './test/TestAppointmentSelection';
import TestClientOnboarding from './test/TestClientOnboarding';
import TestPatientBasics from './test/TestPatientBasics';

const UNSAVED_CHANGES_SCREENS: BookingFlowScreenName[] = [
    'PatientBasics', 'PatientAttributes', 'ReasonForVisit', 'MedicalRecords', 'ClientOnboarding', 'AddCard', 'TestClientOnboarding', 'TestPatientBasics'
];
const GOOGLE_MAPS_LIBRARIES: Libraries = ['places'];
const HEADER_HEIGHT_PX = 72;
const MOBILE_PROGRESS_BAR_HEIGHT_PX = 4;
const DESKTOP_PROGRESS_BAR_HEIGHT_PX = 8;

// Apollo client
const apolloClient = initializeApolloClient('public', null, 'standard');

// Extracting the wrapping of the App component with ApolloProvider
// changes the orders of operations to ensure hooks get called after the client is initialized
const AppWithProvider = () => (
    <ApolloProvider client={apolloClient}>
        <App />
    </ApolloProvider>
);

const App = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';
    const isInBookingFlow = location.pathname === '/';
    const isExistingAccountScreen = location.pathname === '/download-app';
    const { loadError, isLoaded: googleMapsisLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_WEBSITE_SPECIFIC_KEY ?? '',
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    // Get location from URL - needed on app start
    const [searchParams] = useSearchParams();
    const hasLocationParam = !!searchParams.get('location');
    const locationId = searchParams.get('location');

    // Use simplified hook to extract showCreditCardStep from location
    const { showCreditCardStep } = useLocationCreditCardSetting();

    // Initialize with hook value if we have a location param as we should,
    // otherwise fallback to appointment.slot
    const [locationShowCreditCardStep, setLocationShowCreditCardStep] = useState(
        hasLocationParam ? showCreditCardStep : false
    );

    // Track state for test booking flow
    const [useTestBookingFlow, setUseTestBookingFlow] = useState(false);
    const [bookingFlowStep, setBookingFlowStep] = useState(0);

    const {
        onGoToPrevStep, updateOnGoToPrevStep, appointment
    } = useWebBookingStore((state) => ({
        onGoToPrevStep: state.onGoToPrevStep,
        updateOnGoToPrevStep: state.setOnGoToPrevStep,
        appointment: state.appointment,
    }));

    const goToNextStep = useCallback(() => setBookingFlowStep((prevBookingFlowStep) => prevBookingFlowStep + 1), []);

    const goToPrevStep = () => {
        if (onGoToPrevStep) {
            onGoToPrevStep();
            updateOnGoToPrevStep(undefined);
        } else {
            setBookingFlowStep((prevBookingFlowStep) => prevBookingFlowStep - 1);
        }
    };

    const goToScreen = useCallback((screenName: BookingFlowScreenName) => {
        const step = bookingFlowScreens.indexOf(screenName);
        setBookingFlowStep(step);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const BOOKING_FLOW_MAP = useMemo(() => ({
        // Standard screens
        SelectAppointment: <AppointmentSelection key="SelectAppointment" goToNextStep={goToNextStep} />,
        PhoneNumber: <PhoneNumber key="PhoneNumber" goToNextStep={goToNextStep} />,
        PhoneVerification: <PhoneVerification key="PhoneVerification" goToScreen={goToScreen} locationId={locationId} />,
        PatientBasics: <PatientBasics key="PatientBasics" goToNextStep={goToNextStep} />,
        PatientAttributes: <PatientAttributes key="PatientAttributes" goToNextStep={goToNextStep} />,
        ReasonForVisit: <ReasonForVisit key="ReasonForVisit" goToNextStep={goToNextStep} />,
        MedicalRecords: <MedicalRecords key="MedicalRecords" goToNextStep={goToNextStep} />,
        ClientOnboarding: <ClientOnboarding key="ClientOnboarding" locationShowCreditCardStep={locationShowCreditCardStep} goToNextStep={goToNextStep} locationId={locationId} />,
        AddCard: <AddCard key="AddCard" goToNextStep={goToNextStep} />,
        AppointmentConfirmation: <AppointmentConfirmation key="AppointmentConfirmation" locationShowsCreditCardStep={locationShowCreditCardStep} />,

        // Test-specific screens that have different implementations
        TestClientOnboarding: <TestClientOnboarding key="TestClientOnboarding" goToNextStep={goToNextStep} googleMapsisLoaded={googleMapsisLoaded} locationShowCreditCardStep={locationShowCreditCardStep} />,
        TestPatientBasics: <TestPatientBasics key="TestPatientBasics" locationShowCreditCardStep={locationShowCreditCardStep} goToNextStep={goToNextStep} />,
        TestSelectAppointment: <TestAppointmentSelection key="TestSelectAppointment" locationShowCreditCardStep={locationShowCreditCardStep} goToNextStep={goToNextStep} />,
    }), [goToNextStep, goToScreen, locationShowCreditCardStep, googleMapsisLoaded]);

    // Initialize with appropriate booking flow screens based on the feature flag
    const [bookingFlowScreens, setBookingFlowScreens] = useState<BookingFlowScreenName[]>(() => {
        // Start with appropriate base screens based on feature flag
        const baseScreens = useTestBookingFlow
            ? [...TEST_BOOKING_FLOW_SCREENS]
            : [...STANDARD_BOOKING_FLOW_SCREENS];

        // If credit card step should be hidden, filter it out
        if (!locationShowCreditCardStep) {
            return baseScreens.filter((screen) => screen !== 'AddCard');
        }

        return baseScreens;
    });

    const isFinalStep = bookingFlowStep === bookingFlowScreens.length - 1;

    useEffect(() => {
        const currentScreenName = bookingFlowScreens[bookingFlowStep] as BookingFlowScreenName;
        if (UNSAVED_CHANGES_SCREENS.includes(currentScreenName)) {
            const showWarningAlert = (e: BeforeUnloadEvent) => {
                // Return value text is ignored in most browsers, will default to browser implementation.
                // NOTE: There are limitations in which mobile browsers do not support the beforeunload event
                // so this alert will not show up on mobile.
                e.returnValue = 'Changes you made may not be saved';

                // If an appointment has been created already, reset all slices.
                // This covers an edge case in which a user books an appointment, the
                // AA modal pops up before navigating to the appointment confirmation
                // screen, and the user closes/refreshes the tab.
                if (appointment.createdAppointment) {
                    resetAllSlices();
                }
            };

            window.addEventListener('beforeunload', showWarningAlert);
            return () => window.removeEventListener('beforeunload', showWarningAlert);
        }

        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingFlowStep, appointment.createdAppointment]);

    useEffect(() => {
        // Heap tracking for each screen in the booking flow
        const bookingFlowScreenName = bookingFlowScreens[bookingFlowStep];
        heapTrack(`Web Booking - View - ${bookingFlowScreenName}`);
        posthog.capture('web_booking_view', { screen: bookingFlowScreenName });

        // Scroll to top of page every time to change screens
        const webBookingContainer = document.getElementById('web-booking-container');
        if (webBookingContainer) webBookingContainer.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingFlowStep]);

    useEffect(() => {
        if (loadError) console.error('Error loading Google Maps library - ', loadError);
    }, [loadError]);

    // Initialize feature flags only once when app mounts
    useEffect(() => {
        const featureFlagsCallback = () => {
            const isHighImpactFlowEnabled = posthog.isFeatureEnabled('web-booking-high-impact-flow') ?? false;
            setUseTestBookingFlow(isHighImpactFlowEnabled);
        };

        posthog.onFeatureFlags(featureFlagsCallback);

        // Safety timeout in case PostHog never loads or has an error
        const timeoutId = setTimeout(() => {
            console.warn('Feature flags may not have loaded, using defaults');
            featureFlagsCallback();
        }, 3000);

        return () => clearTimeout(timeoutId);
    }, []);

    // Load Heap Analytics and track initial screen view
    // This effect runs after useTestBookingFlow is determined by feature flags
    useEffect(() => {
        loadHeap(Config.HeapAnalyticsMainID);

        // Track the first screen based on current useTestBookingFlow value
        const screenName = useTestBookingFlow ? 'TestClientOnboarding' : 'SelectAppointment';
        heapTrack(`Web Booking - View - ${screenName}`);
        posthog.capture('web_booking_view', { screen: screenName });
    }, [useTestBookingFlow]);

    // Combined effect for updating screens based on both feature flag and credit card settings
    useEffect(() => {
        // 1. Update location credit card step state from URL or slot
        if (hasLocationParam) {
            // If we have a location param, use the setting from the URL
            setLocationShowCreditCardStep(showCreditCardStep);
        } else if (appointment.slot?.location) {
            // Otherwise use the setting from the appointment slot (original behavior)
            setLocationShowCreditCardStep(!!appointment.slot.location.webBookingShowCreditCardStep);
        }

        // 2. Update screens based on both test booking flow and credit card settings
        const baseScreens = useTestBookingFlow
            ? [...TEST_BOOKING_FLOW_SCREENS]
            : [...STANDARD_BOOKING_FLOW_SCREENS];

        if (locationShowCreditCardStep) {
            setBookingFlowScreens(baseScreens);
        } else {
            setBookingFlowScreens(baseScreens.filter((screen) => screen !== 'AddCard'));
        }
    }, [
        appointment.slot,
        locationShowCreditCardStep,
        hasLocationParam,
        showCreditCardStep,
        useTestBookingFlow
    ]);

    // eslint-disable-next-line complexity
    const renderHeader = () => {
        const progressBarWidth = ((bookingFlowStep + 1) / bookingFlowScreens.length) * 100;
        const isTestPatientBasicsScreen = useTestBookingFlow && bookingFlowScreens[bookingFlowStep] === 'TestPatientBasics';
        const homeUrl = Config.BuildVarient === 'PROD' ? 'https://modernanimal.com' : 'https://staging.modernanimal.com';

        return (
            <HeaderContainer>
                <Header>
                    {!isFinalStep && !isTestPatientBasicsScreen && (
                        <BackContainer
                            onClick={() => {
                                if (!isInBookingFlow || bookingFlowStep === 0) {
                                    navigate(-1);
                                } else {
                                    goToPrevStep();
                                }
                            }}
                        >
                            <Icons.ChevronLeft width={24} height={24} fill={colors.GREY_1} />
                        </BackContainer>
                    )}
                    <LogoButton onClick={() => window.open(homeUrl, '_self')}>
                        {isMobile ? (
                            <Icons.MaLogo width={58} height={36} fill={colors.GREY_1} />
                        ) : (
                            <Icons.MaLogoFull width={202} height={20} fill={colors.GREY_1} />
                        )}
                    </LogoButton>
                    {isFinalStep && (
                        <DoneContainer onClick={() => window.open(homeUrl, '_self')}>
                            <Fonts.LabelMedium style={{ color: colors.PRIMARY_PERIWINKLE }}>Done</Fonts.LabelMedium>
                        </DoneContainer>
                    )}
                </Header>
                <ProgressBarContainer
                    style={{
                        height: isMobile ? `${MOBILE_PROGRESS_BAR_HEIGHT_PX}px` : `${DESKTOP_PROGRESS_BAR_HEIGHT_PX}px`
                    }}
                >
                    <ProgressBarFilled widthPercentage={isInBookingFlow ? progressBarWidth : 100} />
                </ProgressBarContainer>
            </HeaderContainer>
        );
    };

    const renderSteps = () => {
        if (!useTestBookingFlow || isFinalStep || isExistingAccountScreen) return null;

        return (
            <StepContainer isMobile={isMobile}>
                Step
                {' '}
                {<span style={{ color: colors.PRIMARY_PERIWINKLE }}>{bookingFlowStep + 1}</span>}
                {' '}
                of
                {' '}
                {bookingFlowScreens.length - 1}
            </StepContainer>
        );
    };

    return (
        <WebBookingContainer id="web-booking-container" isMobile={isMobile}>
            <Global styles={globalStyles} />
            <HeapSubscriptions />
            {renderHeader()}
            <OuterBodyContainer isMobile={isMobile}>
                {!isMobile && renderSteps()}
                <InnerBodyContainer isMobile={isMobile}>
                    {isMobile && renderSteps()}
                    {isInBookingFlow ? (
                        bookingFlowScreens[bookingFlowStep]
                        && BOOKING_FLOW_MAP[bookingFlowScreens[bookingFlowStep] as keyof typeof BOOKING_FLOW_MAP]
                    ) : (
                        <Outlet />
                    )}
                </InnerBodyContainer>
            </OuterBodyContainer>
        </WebBookingContainer>
    );
};

export default AppWithProvider;

const WebBookingContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    background-color: ${colors.KIOSK_GREY};
    height: ${({ isMobile }) => {
        const progressBarHeight = isMobile ? MOBILE_PROGRESS_BAR_HEIGHT_PX : DESKTOP_PROGRESS_BAR_HEIGHT_PX;
        return `calc(100svh - ${HEADER_HEIGHT_PX + progressBarHeight}px);`;
    }};
    margin-top: ${({ isMobile }) => {
        const progressBarHeight = isMobile ? MOBILE_PROGRESS_BAR_HEIGHT_PX : DESKTOP_PROGRESS_BAR_HEIGHT_PX;
        return `${HEADER_HEIGHT_PX + progressBarHeight}px;`; // Offset for fixed header & progress bar
    }}

    ${({ isMobile }) => !isMobile && `
        overflow-y: scroll; // Don't add overflow-y on mobile or else will cause funky scrolling
    `}
`;

const BackContainer = styled.button`
    display: flex;
    position: absolute;
    left: 24px;
`;

const DoneContainer = styled.button`
    display: flex;
    position: absolute;
    right: 24px;
`;

const Header = styled.div`
    display: flex;
    height: ${HEADER_HEIGHT_PX}px;
    width: 100%;
    justify-content: center;
    align-items: center;
    background-color: ${colors.WHITE};
    position: relative;
`;

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1;
`;

const InnerBodyContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    width: 480px;
    flex-direction: column;
    height: 100%;

    ${({ isMobile }) => isMobile && `
        width: 100%;
        background-color: ${colors.WHITE};
    `}
`;

const LogoButton = styled.button`
    display: flex;
`;

const OuterBodyContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;

    ${({ isMobile }) => !isMobile && `
        align-items: center;
        width: ${MOBILE_WIDTH_PX}px;
        padding: 32px 0px;
        border-radius: 32px;
        background-color: ${colors.WHITE};
        border: 1px solid ${colors.PERIWINKLE_4};
        margin: 40px 0px;
        height: auto;
    `}
`;

const ProgressBarContainer = styled.div`
    display: flex;
    width: 100%;
    background-color: ${colors.GREY_6};
`;

const ProgressBarFilled = styled.div<{ widthPercentage: number }>`
    display: flex;
    background-color: ${colors.PRIMARY_PERIWINKLE};
    width: ${({ widthPercentage }) => `${widthPercentage}%`};
    transition: width 0.3s ease-in-out;
`;

const StepContainer = styled(Fonts.Heading06)<{ isMobile: boolean }>`
    display: flex;
    gap: 4px;
    position: relative;
    right: ${({ isMobile }) => (!isMobile ? '32px' : '6px')};
    width: 100%;
    margin: ${({ isMobile }) => (isMobile ? '16px 0px -8px 0px' : '0px 0px -16px 0px')};
    justify-content: ${({ isMobile }) => (isMobile ? 'center' : 'right')};;
    align-items: center;
`;
